import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import type { WorkspaceAccountScope } from "../../../types/workspaceAccountScope";
import type {
  StrategyWorkspaceContribution,
  WorkspaceDetail,
  WorkspaceViewMode,
} from "../../../types/workspace";
import { mergeWorkspaceDetailPreservingOptimistic } from "./workspaceOptimisticReport";
import {
  DEFAULT_WORKSPACE_LAYOUT_MODE,
  type WorkspaceLayoutMode,
} from "./workspaceLayoutMode";

export type WorkspaceContextValue = {
  scope: WorkspaceAccountScope;
  workspaceId: string;
  viewMode: WorkspaceViewMode;
  /** Set when viewMode === "strategy". */
  strategyId: string | null;
  strategyName: string | null;
  contributingWorkspaces: StrategyWorkspaceContribution[];
  /** Strategy canvas only — Parallel vs Sequential packing. */
  layoutMode: WorkspaceLayoutMode;
  setLayoutMode: (mode: WorkspaceLayoutMode) => void;
  detail: WorkspaceDetail | null;
  loading: boolean;
  error: string | null;
  selectedNodeId: string | null;
  setSelectedNodeId: (nodeId: string | null) => void;
  /** Ordered multi-select (policy combine). First id is the adopted-report anchor. */
  selectedNodeIds: string[];
  setSelectedNodeIds: (nodeIds: string[]) => void;
  /** True after user chooses Combine policies from a policy menu — canvas picks sources. */
  policyCombineMode: boolean;
  enterPolicyCombineMode: (anchorPolicyNodeId: string) => void;
  exitPolicyCombineMode: () => void;
  setDetail: React.Dispatch<React.SetStateAction<WorkspaceDetail | null>>;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  refreshDetail: () => Promise<WorkspaceDetail | null>;
  patchNodePositions: (positions: Array<{ nodeId: string; x: number; y: number }>) => void;
};

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

export type WorkspaceLoadResult = {
  detail: WorkspaceDetail;
  strategyName?: string | null;
  contributingWorkspaces?: StrategyWorkspaceContribution[];
};

export type WorkspaceProviderProps = {
  scope: WorkspaceAccountScope;
  workspaceId: string;
  viewMode?: WorkspaceViewMode;
  strategyId?: string | null;
  initialDetail?: WorkspaceDetail | null;
  loadDetail: (
    scope: WorkspaceAccountScope,
    workspaceId: string
  ) => Promise<WorkspaceDetail | WorkspaceLoadResult>;
  children: React.ReactNode;
};

function unwrapLoadResult(result: WorkspaceDetail | WorkspaceLoadResult): WorkspaceLoadResult {
  if (result && typeof result === "object" && "detail" in result) {
    return result;
  }
  return { detail: result };
}

export function WorkspaceProvider({
  scope,
  workspaceId,
  viewMode = "account",
  strategyId = null,
  initialDetail = null,
  loadDetail,
  children,
}: WorkspaceProviderProps): React.ReactElement {
  const [detail, setDetail] = useState<WorkspaceDetail | null>(initialDetail);
  const [loading, setLoading] = useState(!initialDetail);
  const [error, setError] = useState<string | null>(null);
  const [selectedNodeId, setSelectedNodeIdState] = useState<string | null>(null);
  const [selectedNodeIds, setSelectedNodeIdsState] = useState<string[]>([]);
  const [policyCombineMode, setPolicyCombineMode] = useState(false);
  const [strategyName, setStrategyName] = useState<string | null>(null);
  const [contributingWorkspaces, setContributingWorkspaces] = useState<
    StrategyWorkspaceContribution[]
  >([]);
  const [layoutMode, setLayoutMode] = useState<WorkspaceLayoutMode>(
    DEFAULT_WORKSPACE_LAYOUT_MODE
  );
  const detailRef = useRef(detail);
  detailRef.current = detail;

  const setSelectedNodeId = useCallback((nodeId: string | null) => {
    setSelectedNodeIdState(nodeId);
    setSelectedNodeIdsState(nodeId ? [nodeId] : []);
    if (!nodeId) setPolicyCombineMode(false);
  }, []);

  const setSelectedNodeIds = useCallback((nodeIds: string[]) => {
    const cleaned = nodeIds.map((id) => id.trim()).filter(Boolean);
    setSelectedNodeIdsState(cleaned);
    setSelectedNodeIdState(cleaned.length > 0 ? cleaned[cleaned.length - 1]! : null);
  }, []);

  const enterPolicyCombineMode = useCallback((anchorPolicyNodeId: string) => {
    const id = anchorPolicyNodeId.trim();
    if (!id) return;
    setPolicyCombineMode(true);
    setSelectedNodeIdsState([id]);
    setSelectedNodeIdState(id);
  }, []);

  const exitPolicyCombineMode = useCallback(() => {
    setPolicyCombineMode(false);
    setSelectedNodeIdState(null);
    setSelectedNodeIdsState([]);
  }, []);

  const refreshDetail = useCallback(async (): Promise<WorkspaceDetail | null> => {
    const soft = detailRef.current != null;
    if (!soft) setLoading(true);
    setError(null);
    try {
      const loaded = unwrapLoadResult(await loadDetail(scope, workspaceId));
      let merged = loaded.detail;
      setDetail((current) => {
        merged = mergeWorkspaceDetailPreservingOptimistic(current, loaded.detail);
        return merged;
      });
      if (viewMode === "strategy") {
        setStrategyName(loaded.strategyName?.trim() || loaded.detail.name || null);
        setContributingWorkspaces(loaded.contributingWorkspaces ?? []);
      }
      return merged;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load workspace.";
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [loadDetail, scope, viewMode, workspaceId]);

  const patchNodePositions = useCallback(
    (positions: Array<{ nodeId: string; x: number; y: number }>) => {
      if (positions.length === 0) return;

      setDetail((current) => {
        if (!current) return current;

        const positionById = new Map(
          positions.map((entry) => [entry.nodeId, { x: entry.x, y: entry.y }])
        );

        return {
          ...current,
          nodes: current.nodes.map((node) => {
            const nextPosition =
              positionById.get(node.id) ??
              (node.meta?.serverNodeId
                ? positionById.get(node.meta.serverNodeId)
                : undefined);
            return nextPosition ? { ...node, position: nextPosition } : node;
          }),
        };
      });
    },
    []
  );

  const value = useMemo<WorkspaceContextValue>(
    () => ({
      scope,
      workspaceId,
      viewMode,
      strategyId: strategyId?.trim() || null,
      strategyName,
      contributingWorkspaces,
      layoutMode,
      setLayoutMode,
      detail,
      loading,
      error,
      selectedNodeId,
      setSelectedNodeId,
      selectedNodeIds,
      setSelectedNodeIds,
      policyCombineMode,
      enterPolicyCombineMode,
      exitPolicyCombineMode,
      setDetail,
      setLoading,
      setError,
      refreshDetail,
      patchNodePositions,
    }),
    [
      contributingWorkspaces,
      detail,
      enterPolicyCombineMode,
      error,
      exitPolicyCombineMode,
      layoutMode,
      loading,
      patchNodePositions,
      policyCombineMode,
      refreshDetail,
      scope,
      selectedNodeId,
      selectedNodeIds,
      setLayoutMode,
      setSelectedNodeId,
      setSelectedNodeIds,
      strategyId,
      strategyName,
      viewMode,
      workspaceId,
    ]
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspaceContext(): WorkspaceContextValue {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) {
    throw new Error("useWorkspaceContext must be used within WorkspaceProvider");
  }
  return ctx;
}
